import React, { useEffect, useState } from 'react';
import { tacticalApi, managerApi } from '../lib/api';
import { Toaster, toast } from 'sonner';

import { useTacticalAutosave } from './tactical-editor/useTacticalAutosave';
import { TacticalEditorHeader } from './tactical-editor/TacticalEditorHeader';
import { DevelopmentSection } from './tactical-editor/DevelopmentSection';
import { ScorecardSection } from './tactical-editor/ScorecardSection';
import { FlywheelSection, isFlywheelEra } from './tactical-editor/FlywheelSection';
import { RiscSection, isRiscEra } from './tactical-editor/RiscSection';
import { LightningRoundSection } from './tactical-editor/LightningRoundSection';
import { FacilityOverviewSection } from './tactical-editor/FacilityOverviewSection';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const TacticalEditor = ({ tactical, onBack, onUpdate, isOwner = true }) => {
  const [formData, setFormData] = useState({
    development: tactical?.development || { personal: '', professional: '', soft_skills: '' },
    scorecard: tactical?.scorecard || [],
    lightning_round: tactical?.lightning_round || { priority1: '', priority2: '', priority3: '' },
    risc: tactical?.risc || null,
    facility_overview: tactical?.facility_overview || [],
    facility_sites: tactical?.facility_sites || [],
  });

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Facility sites state
  const [activeSiteId, setActiveSiteId] = useState(null);

  // TAH (Therapy @Home) state
  const [hasTah, setHasTah] = useState(tactical?.has_tah || false);
  const [tahLoading, setTahLoading] = useState(false);

  // Autosave + manual-save orchestration
  const { saveStatus, handleSaveWithStatus, saving } = useTacticalAutosave({
    tactical,
    formData,
    onUpdate,
    isOwner,
  });

  // Set initial active site
  useEffect(() => {
    if (formData.facility_sites.length > 0 && !activeSiteId) {
      const sortedSites = [...formData.facility_sites].sort((a, b) => (a.order || 0) - (b.order || 0));
      setActiveSiteId(sortedSites[0]?.id);
    }
  }, [formData.facility_sites, activeSiteId]);

  const activeSite = formData.facility_sites.find((s) => s.id === activeSiteId);

  const monthName = MONTHS[tactical?.month - 1] || '';
  const year = tactical?.year || new Date().getFullYear();

  // Check if there are unacknowledged manager changes
  const hasUnacknowledgedChanges =
    (tactical?.manager_changes || []).some((c) => !c.acknowledged) ||
    (tactical?.facility_overview || []).some((item) => item.manager_modified);

  const handleAcknowledgeChanges = async () => {
    try {
      await tacticalApi.acknowledgeChanges(tactical.id);
      const updated = await tacticalApi.getCurrent();
      onUpdate(updated);
      toast.success('Manager changes acknowledged');
    } catch (err) {
      console.error('Acknowledge failed:', err);
      toast.error('Failed to acknowledge changes');
    }
  };

  const handleDevelopmentChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      development: { ...prev.development, [field]: value },
    }));
  };

  const handleScorecardChange = (index, value) => {
    setFormData((prev) => {
      const newScorecard = [...prev.scorecard];
      newScorecard[index] = { ...newScorecard[index], status: value };
      return { ...prev, scorecard: newScorecard };
    });
  };

  const handleLightningRoundChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      lightning_round: { ...prev.lightning_round, [field]: value },
    }));
  };

  const handleRiscChange = (nextRisc) => {
    setFormData((prev) => ({ ...prev, risc: nextRisc }));
  };

  const handleFacilityItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.facility_overview];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, facility_overview: newItems };
    });
  };

  // Handle changes to facility site items
  const handleSiteItemChange = (itemIndex, field, value) => {
    if (!activeSiteId) return;

    setFormData((prev) => {
      const newSites = prev.facility_sites.map((site) => {
        if (site.id === activeSiteId) {
          const newItems = [...(site.items || [])];
          newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
          return { ...site, items: newItems };
        }
        return site;
      });
      return { ...prev, facility_sites: newSites };
    });
  };

  // Refresh tactical data after site changes.
  // Only re-pull the site/TAH structure that the server just changed — the
  // other sections are merged from LOCAL state so a long editing session is
  // never clobbered by the server's copy. Overwriting all of formData here
  // used to wipe whatever the user had typed but not yet autosaved.
  const handleSitesUpdated = async () => {
    try {
      const updated = await tacticalApi.getCurrent();
      if (updated) {
        setFormData((prev) => ({
          ...prev,
          facility_sites: updated.facility_sites || [],
        }));
        setHasTah(updated.has_tah || false);
        onUpdate(updated, { silent: true });
      }
    } catch (err) {
      console.error('Failed to refresh tactical:', err);
    }
  };

  // TAH (Therapy @Home) toggle handler
  const handleTahToggle = async (checked) => {
    if (tahLoading) return;

    setTahLoading(true);
    try {
      if (checked) {
        const result = await tacticalApi.enableTah(tactical.id);
        toast.success('TAH (Therapy @Home) enabled');
        await handleSitesUpdated();
        if (result.site) {
          setActiveSiteId(result.site.id);
        }
      } else {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('This will remove the TAH (Therapy @Home) tab and all its data. Continue?')) {
          setTahLoading(false);
          return;
        }
        await tacticalApi.disableTah(tactical.id);
        toast.success('TAH (Therapy @Home) disabled');
        await handleSitesUpdated();
      }
      setHasTah(checked);
    } catch (err) {
      console.error('TAH toggle failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to toggle TAH');
    } finally {
      setTahLoading(false);
    }
  };

  const handleInviteManager = async () => {
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      await managerApi.invite(inviteEmail);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInviteDialog(false);
    } catch (err) {
      console.error('Invite failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRollover = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('This will archive the current tactical and create a new one for next month. Continue?')) {
      return;
    }

    try {
      await tacticalApi.rollover();
      toast.success('Tactical rolled over to next month');
      onBack();
    } catch (err) {
      console.error('Rollover failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to rollover tactical');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="tactical-editor">
      <Toaster position="top-right" />

      <TacticalEditorHeader
        monthName={monthName}
        year={year}
        isOwner={isOwner}
        hasUnacknowledgedChanges={hasUnacknowledgedChanges}
        onBack={onBack}
        onAcknowledgeChanges={handleAcknowledgeChanges}
        onRollover={handleRollover}
        showInviteDialog={showInviteDialog}
        setShowInviteDialog={setShowInviteDialog}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        onInviteManager={handleInviteManager}
        inviting={inviting}
        saveStatus={saveStatus}
        onSave={handleSaveWithStatus}
        saving={saving}
      />

      {/* Tactical Sheet */}
      <main className="max-w-5xl mx-auto px-4 py-4">
        <div className="tactical-sheet rounded-lg overflow-hidden">
          <DevelopmentSection
            development={formData.development}
            onChange={handleDevelopmentChange}
          />

          {isFlywheelEra(tactical?.month, tactical?.year) ? (
            <FlywheelSection />
          ) : (
            <ScorecardSection
              scorecard={formData.scorecard}
              onChange={handleScorecardChange}
            />
          )}

          <LightningRoundSection
            lightningRound={formData.lightning_round}
            onChange={handleLightningRoundChange}
          />

          {isRiscEra(tactical?.month, tactical?.year) && (
            <RiscSection
              risc={formData.risc}
              onChange={handleRiscChange}
              readOnly={!isOwner}
            />
          )}

          <FacilityOverviewSection
            tacticalId={tactical?.id}
            facilitySites={formData.facility_sites}
            facilityOverview={formData.facility_overview}
            activeSiteId={activeSiteId}
            activeSite={activeSite}
            hasTah={hasTah}
            tahLoading={tahLoading}
            isOwner={isOwner}
            onSiteChange={setActiveSiteId}
            onSitesUpdated={handleSitesUpdated}
            onSiteItemChange={handleSiteItemChange}
            onLegacyItemChange={handleFacilityItemChange}
            onTahToggle={handleTahToggle}
          />
        </div>
      </main>
    </div>
  );
};

export default TacticalEditor;
